import React, { useState, useEffect } from 'react';
import { quotesService, companiesService, invoicesService } from '../services/api';
import AdvancedQuoteDialog from '../components/AdvancedQuoteDialog';
import QuotePreviewDialog from '../components/QuotePreviewDialog';
import { logger } from '../utils/logger';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Send as SendIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  PlayArrow as ValidateIcon,
  Receipt as InvoiceIcon,
  CheckCircle as AcceptIcon,
  Cancel as RejectIcon,
  FileCopy as CopyIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  HowToReg as SignIcon
} from '@mui/icons-material';

const Quotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [previewQuote, setPreviewQuote] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [openEmailDialog, setOpenEmailDialog] = useState(false);
  const [emailQuote, setEmailQuote] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState('');
  const [emailError, setEmailError] = useState('');
  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [signingQuote, setSigningQuote] = useState(null);
  const [signForm, setSignForm] = useState({ name: '', email: '' });
  const [signLoading, setSignLoading] = useState(false);
  const [signError, setSignError] = useState('');
  const [signSuccess, setSignSuccess] = useState('');
  const [searchFilters, setSearchFilters] = useState({
    searchTerm: '',
    minAmount: '',
    maxAmount: '',
    dateFrom: '',
    dateTo: '',
    company: '',
    status: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Charger les devis et clients depuis l'API MySQL
  const loadData = async () => {
    try {
      // Charger les clients depuis l'API
      const companiesData = await companiesService.getAll();
      setCompanies(companiesData);

      // Charger les devis depuis l'API
      const quotesData = await quotesService.getAll();
      setQuotes(quotesData);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setError('Erreur lors du chargement des données');
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Télécharger le PDF d'un devis
  const handleDownloadPDF = async (quote) => {
    try {
      const blob = await quotesService.downloadPdf(quote.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Devis-${quote.number}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      logger.error('Erreur téléchargement PDF devis:', err);
      setError(err?.message || 'Erreur lors du téléchargement du PDF');
    }
  };

  // Ouvrir le modal d'envoi d'email
  const handleOpenEmailDialog = (quote) => {
    setEmailQuote(quote);
    setRecipientEmail(quote.company?.email || '');
    setEmailSuccess('');
    setEmailError('');
    setOpenEmailDialog(true);
  };

  // Fermer le modal d'envoi d'email
  const handleCloseEmailDialog = () => {
    setOpenEmailDialog(false);
    setEmailQuote(null);
    setRecipientEmail('');
    setEmailSending(false);
    setEmailSuccess('');
    setEmailError('');
  };

  const handleOpenSignDialog = (quote) => {
    setSigningQuote(quote);
    setSignForm({
      name: quote?.company?.name ? `${quote.company.name} (client)` : '',
      email: quote?.company?.email || ''
    });
    setSignError('');
    setSignSuccess('');
    setSignDialogOpen(true);
  };

  const handleCloseSignDialog = () => {
    setSignDialogOpen(false);
    setSigningQuote(null);
    setSignLoading(false);
    setSignError('');
    setSignSuccess('');
  };

  const handleSignQuote = async () => {
    if (!signingQuote) return;
    if (!signForm.name || !signForm.email) {
      setSignError('Nom et email requis');
      return;
    }

    setSignLoading(true);
    setSignError('');
    setSignSuccess('');

    try {
      await quotesService.signQuote(signingQuote.id, {
        name: signForm.name,
        email: signForm.email,
      });
      setSignSuccess('Devis signé avec succès');
      await loadData();
      setTimeout(() => handleCloseSignDialog(), 1000);
    } catch (error) {
      console.error('Erreur signature devis:', error);
      setSignError(error?.response?.data?.error || 'Erreur lors de la signature');
    } finally {
      setSignLoading(false);
    }
  };

  // Envoyer le devis par email
  const handleSendEmail = async () => {
    if (!recipientEmail) {
      setEmailError('Veuillez saisir une adresse email');
      return;
    }

    setEmailSending(true);
    setEmailError('');
    setEmailSuccess('');

    try {
      const data = await quotesService.sendEmail(emailQuote.id, recipientEmail);
      setEmailSuccess(`✅ Devis envoyé avec succès à ${recipientEmail}`);
      if (data?.previewUrl) {
        logger.debug('🔗 Lien de prévisualisation:', data.previewUrl);
      }
      setTimeout(() => {
        handleCloseEmailDialog();
      }, 2000);
    } catch (error) {
      console.error("Erreur lors de l'envoi:", error);
      setEmailError(error?.response?.data?.error || "Erreur lors de l'envoi de l'email");
    } finally {
      setEmailSending(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT': return 'default';
      case 'SENT': return 'primary';
      case 'ACCEPTED': return 'success';
      case 'REJECTED': return 'error';
      case 'EXPIRED': return 'warning';
      case 'CONVERTED': return 'info';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'DRAFT': return 'Brouillon';
      case 'SENT': return 'Envoyé';
      case 'ACCEPTED': return 'Accepté';
      case 'REJECTED': return 'Refusé';
      case 'EXPIRED': return 'Expiré';
      case 'CONVERTED': return 'Converti en facture';
      default: return status;
    }
  };

  // Fonction pour déterminer les actions disponibles selon le statut
  const getAvailableActions = (quote) => {
    const actions = [];
    const hasLinkedInvoice = Boolean(quote?.invoiceNumber) || (Array.isArray(quote?.invoices) && quote.invoices.length > 0);
    
    // PDF toujours disponible
    actions.push({
      icon: <PdfIcon />,
      onClick: () => handlePreviewQuote(quote),
      color: 'info',
      title: 'Prévisualiser'
    });
    
    // Téléchargement PDF
    actions.push({
      icon: <DownloadIcon />,
      onClick: () => handleDownloadPDF(quote),
      color: 'success',
      title: 'Télécharger PDF'
    });
    
    // Envoi par email
    actions.push({
      icon: <SendIcon />,
      onClick: () => handleOpenEmailDialog(quote),
      color: 'secondary',
      title: 'Envoyer par email'
    });
    
    // Actions selon le statut
    switch (quote.status) {
      case 'DRAFT':
        actions.push(
          {
            icon: <EditIcon />,
            onClick: () => handleEdit(quote),
            color: 'primary',
            title: 'Modifier'
          },
          {
            icon: <ValidateIcon />,
            onClick: () => handleValidateQuote(quote),
            color: 'success',
            title: 'Valider et envoyer'
          },
          {
            icon: <RejectIcon />,
            onClick: () => handleRejectQuote(quote),
            color: 'error',
            title: 'Rejeter'
          }
        );
        break;
        
      case 'SENT':
        actions.push(
          {
            icon: <AcceptIcon />,
            onClick: () => handleAcceptQuote(quote),
            color: 'success',
            title: 'Marquer comme accepté'
          },
          {
            icon: <RejectIcon />,
            onClick: () => handleRejectQuote(quote),
            color: 'error',
            title: 'Rejeter'
          },
          ...(quote.signatureStatus !== 'SIGNED' ? [{
            icon: <SignIcon />,
            onClick: () => handleOpenSignDialog(quote),
            color: 'info',
            title: 'Signer (client)'
          }] : []),
          {
            icon: <EditIcon />,
            onClick: () => handleEdit(quote),
            color: 'primary',
            title: 'Modifier'
          }
        );
        break;
        
      case 'ACCEPTED':
        actions.push({
          icon: <InvoiceIcon />,
          onClick: () => handleConvertToInvoice(quote),
          color: 'primary',
          title: 'Convertir en facture'
        });

        if (quote.signatureStatus !== 'SIGNED') {
          actions.push({
            icon: <SignIcon />,
            onClick: () => handleOpenSignDialog(quote),
            color: 'info',
            title: 'Signer (client)'
          });
        }
        break;
        
      case 'CONVERTED':
        // Aucune action supplémentaire pour les devis convertis
        break;
        
      case 'REJECTED':
        actions.push({
          icon: <CopyIcon />,
          onClick: () => handleCopyQuote(quote),
          color: 'info',
          title: 'Copier ce devis'
        });
        break;
        
      default:
        actions.push({
          icon: <EditIcon />,
          onClick: () => handleEdit(quote),
          color: 'primary',
          title: 'Modifier'
        });
    }
    
    // Duplication toujours disponible
    actions.push({
      icon: <CopyIcon />,
      onClick: () => handleCopyQuote(quote),
      color: 'secondary',
      title: 'Dupliquer ce devis'
    });
    
    // Suppression disponible uniquement si aucune facture n'est liée (et par compat, si pas CONVERTED)
    if (!hasLinkedInvoice && quote.status !== 'CONVERTED') {
      actions.push({
        icon: <DeleteIcon />,
        onClick: () => handleDelete(quote.id),
        color: 'error',
        title: 'Supprimer'
      });
    }
    
    return actions;
  };

  const handleNewQuote = () => {
    setEditingQuote(null);
    setOpenDialog(true);
  };

  const handleSaveQuote = async (quoteData) => {
    try {
      setError(null);
      
      if (editingQuote) {
        await quotesService.update(editingQuote.id, quoteData);
        setSuccess('Devis modifié avec succès !');
      } else {
        await quotesService.create(quoteData);
        setSuccess('Devis créé avec succès !');
      }
      await loadData();
      handleCloseDialog();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setError('Erreur lors de la sauvegarde du devis');
    }
  };

  const handleDelete = async (quoteId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce devis ?')) {
      try {
        setError(null);
        await quotesService.delete(quoteId);
        await loadData();
        setSuccess('Devis supprimé avec succès !');
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        setError('Erreur lors de la suppression du devis');
      }
    }
  };

  const handleEdit = (quote) => {
    setEditingQuote(quote);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingQuote(null);
    // Clear messages after a delay
    setTimeout(() => {
      setError(null);
      setSuccess(null);
    }, 3000);
  };

  const handlePreviewQuote = (quote) => {
    setPreviewQuote(quote);
    setOpenPreview(true);
  };

  const handleClosePreview = () => {
    setOpenPreview(false);
    setPreviewQuote(null);
  };

  const handleValidateQuote = async (quote) => {
    if (window.confirm(`Êtes-vous sûr de vouloir valider le devis ${quote.number} ?`)) {
      try {
        setError(null);
        await quotesService.update(quote.id, { ...quote, status: 'SENT' });
        await loadData();
        setSuccess(`Devis ${quote.number} validé et envoyé !`);
      } catch (error) {
        console.error('Erreur lors de la validation:', error);
        setError('Erreur lors de la validation du devis');
      }
    }
  };

  const handleAcceptQuote = async (quote) => {
    if (window.confirm(`Êtes-vous sûr de vouloir accepter le devis ${quote.number} ?`)) {
      try {
        setError(null);
        await quotesService.update(quote.id, { ...quote, status: 'ACCEPTED' });
        await loadData();
        setSuccess(`Devis ${quote.number} accepté !`);
      } catch (error) {
        console.error('Erreur lors de l\'acceptation:', error);
        setError('Erreur lors de l\'acceptation du devis');
      }
    }
  };

  const handleConvertToInvoice = async (quote) => {
    if (window.confirm(`Êtes-vous sûr de vouloir convertir le devis ${quote.number} en facture ?`)) {
      try {
        setError(null);
        
        // Créer la facture via l'API
        const invoiceData = {
          companyId: quote.companyId,
          date: new Date().toISOString().split('T')[0],
          dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          status: 'DRAFT',
          totalHT: quote.totalHT,
          totalTTC: quote.totalTTC,
          vatAmount: quote.vatAmount,
          paidAmount: 0,
          notes: quote.notes,
          internalNotes: quote.internalNotes,
          items: quote.items || []
        };

        await invoicesService.createFromQuote(quote.id, invoiceData);
        await loadData();
        
        setSuccess(`Devis ${quote.number} converti en facture avec succès !`);
      } catch (error) {
        console.error('Erreur lors de la conversion:', error);
        setError('Erreur lors de la conversion en facture');
      }
    }
  };

  // Fonction pour rejeter un devis
  const handleRejectQuote = async (quote) => {
    if (window.confirm('Êtes-vous sûr de vouloir rejeter ce devis ?')) {
      try {
        setError(null);
        await quotesService.update(quote.id, { ...quote, status: 'REJECTED' });
        await loadData();
        setSuccess('Devis rejeté');
      } catch (error) {
        console.error('Erreur lors du rejet:', error);
        setError('Erreur lors du rejet du devis');
      }
    }
  };

  // Fonction pour copier un devis
  const handleCopyQuote = async (quote) => {
    try {
      setError(null);
      const newQuoteData = {
        companyId: quote.companyId,
        date: new Date().toISOString().split('T')[0],
        validUntil: quote.validUntil,
        status: 'DRAFT',
        totalHT: quote.totalHT,
        totalTTC: quote.totalTTC,
        vatAmount: quote.vatAmount,
        notes: quote.notes,
        internalNotes: quote.internalNotes,
        items: quote.items
      };
      
      await quotesService.create(newQuoteData);
      await loadData();
      setSuccess('Devis copié avec succès !');
    } catch (error) {
      console.error('Erreur lors de la copie:', error);
      setError('Erreur lors de la copie du devis');
    }
  };

  // Fonction de filtrage des devis
  const filteredQuotes = quotes.filter(quote => {
    // Filtre par terme de recherche (numéro, objet, client)
    if (searchFilters.searchTerm) {
      const searchLower = searchFilters.searchTerm.toLowerCase();
      const matchesSearch = 
        quote.number?.toLowerCase().includes(searchLower) ||
        quote.object?.toLowerCase().includes(searchLower) ||
        quote.company?.name?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Filtre par montant minimum
    if (searchFilters.minAmount && quote.totalTTC < parseFloat(searchFilters.minAmount)) {
      return false;
    }

    // Filtre par montant maximum
    if (searchFilters.maxAmount && quote.totalTTC > parseFloat(searchFilters.maxAmount)) {
      return false;
    }

    // Filtre par date de début
    if (searchFilters.dateFrom && new Date(quote.date) < new Date(searchFilters.dateFrom)) {
      return false;
    }

    // Filtre par date de fin
    if (searchFilters.dateTo && new Date(quote.date) > new Date(searchFilters.dateTo)) {
      return false;
    }

    // Filtre par client
    if (searchFilters.company && quote.companyId !== searchFilters.company) {
      return false;
    }

    // Filtre par statut
    if (searchFilters.status && quote.status !== searchFilters.status) {
      return false;
    }

    return true;
  });

  // Gestion des filtres
  const handleFilterChange = (filterName, value) => {
    setSearchFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearFilters = () => {
    setSearchFilters({
      searchTerm: '',
      minAmount: '',
      maxAmount: '',
      dateFrom: '',
      dateTo: '',
      company: '',
      status: ''
    });
  };

  const stats = {
    total: quotes.length,
    draft: quotes.filter(q => q.status === 'DRAFT').length,
    sent: quotes.filter(q => q.status === 'SENT').length,
    accepted: quotes.filter(q => q.status === 'ACCEPTED').length
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* En-tête */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Gestion des Devis
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNewQuote}
          sx={{ bgcolor: 'primary.main' }}
        >
          Nouveau Devis
        </Button>
      </Box>

      {/* Statistiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Total Devis
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.total}
                  </Typography>
                </Box>
                <DescriptionIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    En Cours
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {stats.draft}
                  </Typography>
                </Box>
                <Chip label="Brouillon" color="default" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Envoyés
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                    {stats.sent}
                  </Typography>
                </Box>
                <SendIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Acceptés
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {stats.accepted}
                  </Typography>
                </Box>
                <CheckIcon sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Section de recherche */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Rechercher des devis
            </Typography>
            <Button 
              variant="outlined" 
              onClick={() => setShowFilters(!showFilters)}
              startIcon={showFilters ? <CloseIcon /> : <SearchIcon />}
            >
              {showFilters ? 'Masquer' : 'Filtres avancés'}
            </Button>
          </Box>
          
          {/* Recherche rapide */}
          <TextField
            fullWidth
            placeholder="Rechercher par numéro, objet ou client..."
            value={searchFilters.searchTerm}
            onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            sx={{ mb: showFilters ? 2 : 0 }}
          />

          {/* Filtres avancés */}
          {showFilters && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Montant min (€)"
                  type="number"
                  value={searchFilters.minAmount}
                  onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Montant max (€)"
                  type="number"
                  value={searchFilters.maxAmount}
                  onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Date de début"
                  type="date"
                  value={searchFilters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Date de fin"
                  type="date"
                  value={searchFilters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Client</InputLabel>
                  <Select
                    value={searchFilters.company}
                    onChange={(e) => handleFilterChange('company', e.target.value)}
                    label="Client"
                  >
                    <MenuItem value="">Toutes</MenuItem>
                    {companies.map(company => (
                      <MenuItem key={company.id} value={company.id}>
                        {company.name} {company.companyType === 'B2C' ? '(Particulier)' : '(Entreprise)'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={searchFilters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    label="Statut"
                  >
                    <MenuItem value="">Tous</MenuItem>
                    <MenuItem value="DRAFT">Brouillon</MenuItem>
                    <MenuItem value="SENT">Envoyé</MenuItem>
                    <MenuItem value="VALIDATED">Validé</MenuItem>
                    <MenuItem value="ACCEPTED">Accepté</MenuItem>
                    <MenuItem value="REJECTED">Rejeté</MenuItem>
                    <MenuItem value="CONVERTED">Converti</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={4}>
                <Button 
                  fullWidth
                  variant="outlined"
                  onClick={clearFilters}
                  sx={{ height: '56px' }}
                >
                  Réinitialiser
                </Button>
              </Grid>
            </Grid>
          )}
          
          {/* Résultats */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {filteredQuotes.length} devis trouvé(s) sur {quotes.length} total
          </Typography>
        </CardContent>
      </Card>

      {/* Liste des devis */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Liste des Devis
          </Typography>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Numéro</strong></TableCell>
                  <TableCell><strong>Client</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Créé par</strong></TableCell>
                  <TableCell><strong>Validité</strong></TableCell>
                  <TableCell><strong>Statut</strong></TableCell>
                  <TableCell align="right"><strong>Montant HT</strong></TableCell>
                  <TableCell align="right"><strong>Montant TTC</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredQuotes.map((quote) => (
                  <TableRow key={quote.id} hover>
                    <TableCell>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {quote.number}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {quote.company?.name || 'Client inconnu'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(quote.date).toLocaleDateString('fr-FR')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {quote.createdBy?.name || quote.user?.name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {quote.validUntil ? new Date(quote.validUntil).toLocaleDateString('fr-FR') : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getStatusLabel(quote.status)}
                        color={getStatusColor(quote.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {quote.totalHT?.toFixed(2)} €
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold" color="primary">
                        {quote.totalTTC?.toFixed(2)} €
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {getAvailableActions(quote).map((action, actionIndex) => (
                          <IconButton
                            key={actionIndex}
                            size="small"
                            color={action.color}
                            onClick={action.onClick}
                            title={action.title}
                            sx={{
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                transform: 'scale(1.1)',
                                boxShadow: 2
                              }
                            }}
                          >
                            {action.icon}
                          </IconButton>
                        ))}
                      </Box>
                      {quote.invoiceNumber && (
                        <Typography variant="caption" color="info.main" sx={{ display: 'block', mt: 0.5 }}>
                          → {quote.invoiceNumber}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Messages d'erreur et succès */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Dialog avancé pour les devis */}
      <AdvancedQuoteDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onSave={handleSaveQuote}
        editingQuote={editingQuote}
        companies={companies}
      />

      {/* Modal d'envoi par email */}
      <Dialog open={openEmailDialog} onClose={handleCloseEmailDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          📧 Envoyer le devis par email
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {emailQuote && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Typography variant="subtitle2" color="text.secondary">Devis</Typography>
                <Typography variant="h6">{emailQuote.number}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Montant: {emailQuote.totalTTC?.toFixed(2)} € TTC
                </Typography>
              </Box>
            )}
            
            <TextField
              fullWidth
              label="Email du destinataire"
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              required
              helperText="Le PDF du devis sera envoyé en pièce jointe"
              sx={{ mb: 2 }}
            />

            {emailSuccess && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {emailSuccess}
              </Alert>
            )}

            {emailError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {emailError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEmailDialog} disabled={emailSending}>
            Annuler
          </Button>
          <Button 
            onClick={handleSendEmail} 
            variant="contained" 
            disabled={emailSending || !recipientEmail}
            startIcon={<SendIcon />}
          >
            {emailSending ? 'Envoi en cours...' : 'Envoyer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de signature (devis) */}
      <Dialog open={signDialogOpen} onClose={handleCloseSignDialog} maxWidth="sm" fullWidth>
        <DialogTitle>✍️ Signature (client)</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {signingQuote && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" color="text.secondary">Devis</Typography>
                <Typography variant="h6">{signingQuote.number}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Client: {signingQuote.company?.name || '-'}
                </Typography>
              </Box>
            )}

            <TextField
              fullWidth
              label="Nom"
              value={signForm.name}
              onChange={(e) => setSignForm((prev) => ({ ...prev, name: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={signForm.email}
              onChange={(e) => setSignForm((prev) => ({ ...prev, email: e.target.value }))}
            />

            {signError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {signError}
              </Alert>
            )}
            {signSuccess && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {signSuccess}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSignDialog} disabled={signLoading}>Annuler</Button>
          <Button
            onClick={handleSignQuote}
            variant="contained"
            disabled={signLoading || !signForm.name || !signForm.email}
            startIcon={<SignIcon />}
          >
            {signLoading ? 'Signature…' : 'Signer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de prévisualisation PDF */}
      <QuotePreviewDialog
        open={openPreview}
        onClose={handleClosePreview}
        quote={previewQuote}
        company={previewQuote ? companies.find(c => c.id === previewQuote.companyId) : null}
      />
    </Box>
  );
};

export default Quotes;